import { cs } from '../../lib/currency';
/**
 * PastUniformOrdersList — shared component used by:
 *   1. UniformVerification (when no active campaign — show past orders
 *      instead of the bare "campaign closed" message)
 *   2. ParentUniformOrders page (dedicated history tab in the parent
 *      portal)
 *
 * Surfaces order summary only: items, sizes, total, order date, status.
 * No actions (parents can't re-edit orders post-campaign).
 *
 * iter307ag (Feb 2026).
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Loader2, Shirt, Calendar, Package } from 'lucide-react';
import api from '../../services/api';

const STATUS_TONE = {
  pending:    'bg-amber-100 text-amber-800 border-amber-200',
  confirmed:  'bg-emerald-100 text-emerald-800 border-emerald-200',
  processed:  'bg-sky-100 text-sky-800 border-sky-200',
  cancelled:  'bg-rose-100 text-rose-700 border-rose-200',
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch {
    return String(iso).slice(0, 10);
  }
};

const formatItems = (items) => {
  if (!Array.isArray(items) || items.length === 0) return '—';
  return items
    .map((it) => {
      const qty = it.quantity || 1;
      const rawName = it.item_name || it.name || it.item_type || 'Item';
      // Humanise snake_case ("full_kit" → "Full Kit").
      const name = String(rawName).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
      const sizeBits = [];
      if (it.size) sizeBits.push(it.size);
      if (it.chest_size) sizeBits.push(`Chest ${it.chest_size}`);
      if (it.waist_size) sizeBits.push(`Waist ${it.waist_size}`);
      if (it.shirt_length) sizeBits.push(`Shirt ${it.shirt_length}`);
      if (it.trouser_length) sizeBits.push(`Trouser ${it.trouser_length}`);
      if (it.sleeves) sizeBits.push(`${it.sleeves} sleeves`);
      const size = sizeBits.length ? ` · ${sizeBits.join(', ')}` : '';
      return `${qty}× ${name}${size}`;
    })
    .join(', ');
};

const PastUniformOrdersList = ({ heading, emptyMessage }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/uniform/orders');
        if (!cancelled) setOrders(res.data || []);
      } catch {
        if (!cancelled) setOrders([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 text-slate-500" data-testid="uniform-history-loading">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        Loading your past orders…
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6 text-center py-12">
          <Shirt className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-700">No Uniform Orders Yet</h3>
          <p className="text-slate-500 mt-2 text-sm">
            {emptyMessage || "You haven't placed any uniform orders yet. Check back when a new campaign opens."}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="uniform-history-list">
      {heading && <h3 className="text-base font-semibold text-slate-800">{heading}</h3>}
      {orders.map((order) => {
        const status = (order.status || 'pending').toLowerCase();
        const total = order.total_amount ?? order.total ?? null;
        return (
          <Card key={order.id} data-testid={`uniform-order-${order.id}`} className="border-slate-200">
            <CardContent className="pt-5 pb-5">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="space-y-1 min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Package className="h-4 w-4 text-slate-500 shrink-0" />
                    <span className="font-semibold text-slate-800 truncate">
                      {order.campaign_title || order.campaign_name || 'Uniform order'}
                    </span>
                    <Badge
                      className={`text-xs ${STATUS_TONE[status] || 'bg-slate-100 text-slate-700 border-slate-200'}`}
                      data-testid={`uniform-order-status-${order.id}`}
                    >
                      {status}
                    </Badge>
                  </div>
                  {(order.player_data?.full_name || order.player_name || order.is_for_parent) && (
                    <p className="text-xs text-slate-500">
                      For{' '}
                      <span className="font-medium text-slate-700">
                        {order.is_for_parent
                          ? 'yourself'
                          : (order.player_data?.full_name || order.player_name)}
                      </span>
                    </p>
                  )}
                  <p className="text-sm text-slate-700 break-words">
                    {formatItems(order.items)}
                  </p>
                  <p className="text-xs text-slate-400 flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {formatDate(order.created_at)}
                  </p>
                </div>
                {total != null && (
                  <div className="text-right shrink-0">
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Total</p>
                    <p className="text-lg font-bold text-slate-800">{cs()}{Number(total).toFixed(2)}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default PastUniformOrdersList;
